import { useTranslation } from 'react-i18next';

import { useCurrency } from '@/hooks/useCurrency';
import {
  ABC_XYZ_KEYS,
  type AbcXyzKey,
} from '@/services/safetyStockService';
import {
  fractionToPercentInput,
  percentInputToFraction,
} from '../utils/configValidation';

const ABC = ['A', 'B', 'C'] as const;
const XYZ = ['X', 'Y', 'Z'] as const;

export type SegmentWeight = {
  items: number;
  value: number;
};

interface Props {
  values: Partial<Record<AbcXyzKey, number>>;
  weights?: Partial<Record<string, SegmentWeight>>;
  disabled?: boolean;
  errors?: Record<string, string>;
  onChange: (key: AbcXyzKey, fraction: number | null) => void;
}

export function ServiceLevelMatrixEditor({
  values,
  weights,
  disabled,
  errors,
  onChange,
}: Props) {
  const { t } = useTranslation();
  const { symbol } = useCurrency();

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500">
              ABC \\ XYZ
            </th>
            {XYZ.map((z) => (
              <th key={z} className="px-2 py-1.5 text-center text-xs font-semibold text-gray-700">
                {z}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ABC.map((a) => (
            <tr key={a}>
              <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">{a}</th>
              {XYZ.map((z) => {
                const key = `${a}${z}` as AbcXyzKey;
                if (!ABC_XYZ_KEYS.includes(key)) return null;
                const err = errors?.[`service_levels.${key}`];
                const weight = weights?.[key];
                return (
                  <td key={key} className="px-2 py-1.5 align-top">
                    <label className="block">
                      <span className="sr-only">{key}</span>
                      <div className="relative">
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min={0.01}
                          max={100}
                          disabled={disabled}
                          value={fractionToPercentInput(values[key])}
                          onChange={(e) => {
                            const fraction = percentInputToFraction(e.target.value);
                            onChange(key, fraction);
                          }}
                          className={[
                            'w-full rounded-md border px-2 py-1.5 pr-7 text-right tabular-nums',
                            err ? 'border-red-400 bg-red-50' : 'border-gray-300',
                            disabled ? 'bg-gray-50 text-gray-500' : 'bg-white',
                          ].join(' ')}
                        />
                        <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">
                          %
                        </span>
                      </div>
                    </label>
                    <p className="mt-1 text-[11px] leading-tight text-gray-400">
                      {weight
                        ? t('safetyStock.parameters.matrix.weight', {
                            items: weight.items,
                            value: weight.value.toFixed(0),
                            currencySymbol: symbol,
                          })
                        : t('safetyStock.parameters.matrix.noWeight')}
                    </p>
                    {err ? (
                      <p className="mt-0.5 text-[11px] text-red-600">
                        {t(`safetyStock.parameters.errors.${err}`)}
                      </p>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
